import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { SyncLoader } from 'react-spinners';
import { StepCard } from '../components/StepCard';
import { setSelectedSlot, setSelectedOperatories } from '../redux/slices/appointmentSlice';
import TimeSlotSelection from '../components/TimeSlotSelection';
import { formatDateToISO } from '../utils/helpers';
import { useIsSmallScreen } from '../hooks/useIsSmallScreen';
import { AvailableSlotsResponse, fetchAvailableSlots } from '../services/api';
import { SMALL_SCREEN_DAYS_TO_SHOW, LARGE_SCREEN_DAYS_TO_SHOW } from '../utils/constants';

export const AvailableSlots = () => {
  const dispatch = useDispatch();
  const patientType = useSelector((s: RootState) => s.appointment.patientType);
  const selectedTreatment = useSelector((s: RootState) => s.appointment.treatment);
  const selectedDurationMinutes = useSelector((s: RootState) => s.appointment.selectedDurationMinutes);
  const selectedSlot = useSelector((s: RootState) => s.appointment.selectedSlot);
  const { practice } = useSelector((s: RootState) => s.auth);
  const primetimeFilter = useSelector((s: RootState) => s.appointment.primetimeFilter);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentStart, setCurrentStart] = useState<Date>(new Date());
  const [slotsByDate, setSlotsByDate] = useState<Record<string, Record<string, string[]>>>({});

  const isSmallScreen = useIsSmallScreen();
  const daysToShow = isSmallScreen ? SMALL_SCREEN_DAYS_TO_SHOW : LARGE_SCREEN_DAYS_TO_SHOW;

  const currentEnd = useMemo(() => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + (daysToShow - 1));
    return d;
  }, [currentStart, daysToShow]);

  const goPrevWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() - daysToShow);
    setCurrentStart(d);
  };

  const goNextWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + daysToShow);
    setCurrentStart(d);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(currentStart),
          end_date: formatDateToISO(currentEnd),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: selectedTreatment?.id,
          ...(practice?.promote_primetime && { filters: primetimeFilter }),
        });
        const map: Record<string, Record<string, string[]>> = {};
        const slotMap: Record<string, string[]> = {};
        for (const operatory of res || []) {
          const operatoryID = operatory?.id;
          const available = operatory?.attributes?.available_slots || {};
          for (const dateKey of Object.keys(available)) {
            const items = available[dateKey] || [];
            if (!map[dateKey]) map[dateKey] = {};
            for (const item of items) {
              // Use start_time as the slot label; keep mapping to pms operatory id
              const display = item.start_time;
              if (!map[dateKey][display]) {
                map[dateKey][display] = [];
              }
              map[dateKey][display].push(operatoryID);
              const slotId = `${dateKey}-${display}`;
              if (slotMap[slotId]) {
                slotMap[slotId].push(operatoryID);
              } else {
                slotMap[slotId] = [operatoryID];
              }
            }
          }
        }
        setSlotsByDate(map);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentStart, currentEnd, patientType, selectedTreatment?.id, selectedDurationMinutes, primetimeFilter]);

  return (
    <StepCard>
      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      ) : (
        <TimeSlotSelection
          selectedSlot={selectedSlot}
          onSlotSelect={(slot) => dispatch(setSelectedSlot(slot))}
          onOperatoriesSelect={(ops) => dispatch(setSelectedOperatories(ops))}
          slotsByDate={slotsByDate}
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          currentStart={currentStart}
          daysToShow={daysToShow}
        />
      )}
    </StepCard>
  );
};
